import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AIChatApi, isSessionGoneError } from './api';
import type {
  AIChatMessage,
  AIChatSession,
  AIChatStreamHandlers,
  CreateAIChatSessionRequest,
  SendAIChatMessageRequest,
  SendAIChatFeedbackRequest,
  SessionStatus,
} from './types';

const QUERY_KEYS = {
  all: ['ai-chat'] as const,
  sessions: () => [...QUERY_KEYS.all, 'sessions'] as const,
  session: (sessionId: string) => [...QUERY_KEYS.all, 'session', sessionId] as const,
  quota: () => [...QUERY_KEYS.all, 'quota'] as const,
  draft: (sessionId: string) => [...QUERY_KEYS.all, 'draft', sessionId] as const,
  documents: (sessionId: string) => [...QUERY_KEYS.all, 'documents', sessionId] as const,
};

/** A draft session's draft; refetched after every turn */
export const useAIDraft = (sessionId: string | null) => {
  return useQuery({
    queryKey: QUERY_KEYS.draft(sessionId ?? ''),
    queryFn: () => AIChatApi.getDraft(sessionId ?? ''),
    enabled: !!sessionId,
    staleTime: 0,
  });
};

/** Lint the draft again; the result replaces the cached draft */
export const useRecheckAIDraft = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (sessionId: string) => AIChatApi.recheckDraft(sessionId),
    onSuccess: (view, sessionId) => queryClient.setQueryData(QUERY_KEYS.draft(sessionId), view),
  });
};

/** Confirm or withdraw a draft item; the result replaces the cached draft */
export const useConfirmAIDraftItem = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ sessionId, itemId, confirmed }: { sessionId: string; itemId: string; confirmed: boolean }) =>
      AIChatApi.confirmDraftItem(sessionId, itemId, confirmed),
    onSuccess: (view, { sessionId }) => queryClient.setQueryData(QUERY_KEYS.draft(sessionId), view),
  });
};

/** Export a ready draft; when it is not ready the fresh view replaces the cached draft */
export const useExportAIDraft = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (sessionId: string) => AIChatApi.exportDraft(sessionId),
    onSuccess: (result, sessionId) => {
      if (!result.ready) {
        queryClient.setQueryData(QUERY_KEYS.draft(sessionId), result.view);
      }
    },
  });
};

/**
 * Create a new AI chat session
 */
export const useCreateAIChatSession = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateAIChatSessionRequest) => AIChatApi.createSession(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.sessions() });
    },
  });
};

/**
 * List the caller's sessions, optionally filtered by status (for resumption)
 */
export const useAIChatSessions = (status?: SessionStatus, enabled: boolean = true) => {
  return useQuery({
    queryKey: [...QUERY_KEYS.sessions(), status ?? 'all'],
    queryFn: () => AIChatApi.listSessions(status),
    enabled,
    staleTime: 0,
  });
};

/**
 * Get a session with its message history, refetched while a turn runs on it
 */
export const useAIChatSession = (sessionId: string) => {
  return useQuery({
    queryKey: QUERY_KEYS.session(sessionId),
    queryFn: () => AIChatApi.getSession(sessionId),
    enabled: !!sessionId,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchInterval: (query) => (query.state.data?.session?.turn_in_progress ? 5000 : false),
  });
};

/**
 * A session a page opened from its URL. onRestore gets the history on load, and again
 * when a turn that was running at load ends, so a reload mid-turn still shows the reply.
 * isGone is true when the session can't take messages, so the next one starts a new
 * session; a session belongs rejects isn't shown at all. problem is what to tell the user
 * when the history didn't load or a resumed turn ended without a reply.
 */
export const useRestoredAIChatSession = (
  sessionId: string,
  onRestore: (messages: AIChatMessage[]) => void,
  belongs: (session: AIChatSession) => boolean
) => {
  const queryClient = useQueryClient();
  const query = useAIChatSession(sessionId);
  const [shown, setShown] = useState<'nothing' | 'mid-turn' | 'all'>('nothing');
  const [resumeFailed, setResumeFailed] = useState(false);
  // A cached copy may predate a turn, when the panel remounts without a page load
  const data = query.isFetchedAfterMount ? query.data : undefined;
  const isForeign = data !== undefined && !belongs(data.session);
  const running = !isForeign && data?.session?.turn_in_progress === true;
  if (data && !isForeign && (shown === 'nothing' || (shown === 'mid-turn' && !running))) {
    if (shown === 'mid-turn' && data.messages[data.messages.length - 1]?.role === 'user') {
      setResumeFailed(true);
    }
    setShown(running ? 'mid-turn' : 'all');
    onRestore(data.messages);
  }
  const isResuming = shown !== 'all' && running;

  // A Build turn changes the draft too
  useEffect(() => {
    if (sessionId && !isResuming) {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.draft(sessionId) });
    }
  }, [queryClient, sessionId, isResuming]);

  const isGone =
    isForeign ||
    isSessionGoneError(query.error) ||
    (data !== undefined && data.session?.session_status !== 'active');
  let problem: string | null = null;
  if (resumeFailed) {
    problem = 'The assistant could not finish your last message. Send it again.';
  } else if (query.isError && !isGone && !data) {
    problem = 'This chat could not be loaded. Reload the page to try again.';
  }
  return { isLoading: !!sessionId && !query.isFetchedAfterMount, isResuming, isGone, isForeign, problem };
};

/**
 * Delete/abandon a session
 */
export const useDeleteAIChatSession = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (sessionId: string) => AIChatApi.deleteSession(sessionId),
    onSuccess: (_, sessionId) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.session(sessionId) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.sessions() });
    },
  });
};

/**
 * Send a message to the AI assistant
 * Automatically refetches the session to update message history
 */
export const useSendAIChatMessage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ sessionId, data }: { sessionId: string; data: SendAIChatMessageRequest }) =>
      AIChatApi.sendMessage(sessionId, data),
    onSuccess: (response, { sessionId }) => {
      // Refetch the session to get updated messages
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.session(sessionId) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.draft(sessionId) });
    },
  });
};

/**
 * Send a message to the AI assistant, streaming the response over SSE.
 * Same invalidation behavior as useSendAIChatMessage.
 */
export const useSendAIChatMessageStream = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      sessionId,
      data,
      handlers,
    }: {
      sessionId: string;
      data: SendAIChatMessageRequest;
      handlers?: AIChatStreamHandlers;
    }) => AIChatApi.sendMessageStream(sessionId, data, handlers),
    onSuccess: (response, { sessionId }) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.session(sessionId) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.draft(sessionId) });
      // A session list shows each session's first message and orders by last use
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.sessions() });
    },
  });
};

/** A Solve session's documents */
export const useAIChatDocuments = (sessionId: string | null) => {
  return useQuery({
    queryKey: QUERY_KEYS.documents(sessionId ?? ''),
    queryFn: () => AIChatApi.listDocuments(sessionId ?? ''),
    enabled: !!sessionId,
  });
};

/** Attach a file to a Solve session */
export const useAddAIChatDocument = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ sessionId, file }: { sessionId: string; file: File }) => AIChatApi.addDocument(sessionId, file),
    onSuccess: (_, { sessionId }) => queryClient.invalidateQueries({ queryKey: QUERY_KEYS.documents(sessionId) }),
  });
};

/** Remove a document from a Solve session */
export const useDeleteAIChatDocument = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ sessionId, documentId }: { sessionId: string; documentId: string }) =>
      AIChatApi.deleteDocument(sessionId, documentId),
    onSuccess: (_, { sessionId }) => queryClient.invalidateQueries({ queryKey: QUERY_KEYS.documents(sessionId) }),
  });
};

/**
 * Record thumbs up/down feedback on an assistant message
 * Refetches the session so the stored feedback shows in message metadata
 */
export const useSendAIChatFeedback = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ sessionId, messageId, data }: { sessionId: string; messageId: string; data: SendAIChatFeedbackRequest }) =>
      AIChatApi.sendFeedback(sessionId, messageId, data),
    onSuccess: (_, { sessionId }) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.session(sessionId) });
    },
  });
};

/**
 * Get organization AI usage quota status
 */
export const useAIChatQuota = (enabled: boolean = true) => {
  return useQuery({
    queryKey: QUERY_KEYS.quota(),
    queryFn: () => AIChatApi.getQuota(),
    enabled,
    staleTime: 60 * 1000, // Cache for 1 minute
    refetchOnWindowFocus: true,
  });
};

// Export query keys for external use
export { QUERY_KEYS as AI_CHAT_QUERY_KEYS };
